'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Framework {
  id: string;
  name: string;
  shortName: string;
  issuingBody: string;
  year: string;
  summary: string;
  keyPrinciples: string[];
  keyRequirements: string[];
  scope: string;
  color: string;
  icon: string;
}

const FRAMEWORKS: Framework[] = [
  {
    id: 'eu-ai-act',
    name: 'EU Artificial Intelligence Act',
    shortName: 'EU AI Act',
    issuingBody: 'European Union',
    year: '2024',
    summary:
      'The EU AI Act is the world\'s first comprehensive legal framework for artificial intelligence, establishing binding obligations for AI systems based on their risk level. It classifies AI systems into four risk tiers — unacceptable, high, limited, and minimal — and imposes requirements proportionate to the potential harm each tier poses to individuals and society.',
    scope: 'Legally binding regulation applicable to all AI systems placed on the EU market or used within the EU, regardless of where the developer is based.',
    keyPrinciples: [
      'Risk-based classification of AI systems',
      'Prohibition of unacceptable-risk AI (e.g. social scoring, real-time biometric surveillance in public)',
      'Mandatory conformity assessments for high-risk AI',
      'Transparency obligations for limited-risk AI (e.g. chatbots)',
      'Fundamental rights impact assessments',
      'Human oversight requirements for high-risk systems',
    ],
    keyRequirements: [
      'Register high-risk AI systems in the EU database',
      'Maintain technical documentation and audit logs',
      'Implement risk management systems throughout the AI lifecycle',
      'Ensure data governance and training data quality',
      'Provide clear information to users about AI interactions',
      'Designate an EU-based authorised representative if outside the EU',
    ],
    color: '#3b82f6',
    icon: '🇪🇺',
  },
  {
    id: 'iso-42001',
    name: 'ISO/IEC 42001',
    shortName: 'ISO/IEC 42001',
    issuingBody: 'International Organization for Standardization (ISO) / International Electrotechnical Commission (IEC)',
    year: '2023',
    summary:
      'ISO/IEC 42001 is the international standard for AI Management Systems (AIMS), providing organisations with a structured framework to govern the responsible development, deployment, and use of AI. It follows the familiar ISO management system structure (Plan-Do-Check-Act) and is designed to be integrated with existing standards such as ISO 27001 and ISO 9001.',
    scope: 'Applicable to any organisation that develops, provides, or uses AI-based products and services, regardless of size, sector, or geography.',
    keyPrinciples: [
      'Accountability and governance for AI systems',
      'Risk-based approach to AI management',
      'Transparency and explainability of AI decisions',
      'Human oversight and control mechanisms',
      'Continuous improvement of AI governance practices',
      'Stakeholder engagement and impact assessment',
    ],
    keyRequirements: [
      'Establish an AI management system with defined scope and policies',
      'Conduct AI risk and impact assessments',
      'Define roles, responsibilities, and competencies for AI governance',
      'Implement controls for data quality, bias, and security',
      'Maintain records and documentation of AI system lifecycle',
      'Conduct internal audits and management reviews',
    ],
    color: '#00d4ff',
    icon: '🏛️',
  },
  {
    id: 'nist-ai-rmf',
    name: 'NIST AI Risk Management Framework',
    shortName: 'NIST AI RMF',
    issuingBody: 'National Institute of Standards and Technology (NIST), United States',
    year: '2023',
    summary:
      'The NIST AI Risk Management Framework provides a voluntary, flexible framework to help organisations identify, assess, and manage risks associated with AI systems throughout their lifecycle. It is structured around four core functions — Govern, Map, Measure, and Manage — and emphasises trustworthiness characteristics including reliability, safety, security, explainability, fairness, and privacy.',
    scope: 'Voluntary framework applicable to any organisation designing, developing, deploying, or using AI systems, with particular relevance to US federal agencies and their contractors.',
    keyPrinciples: [
      'Trustworthiness as the foundation of responsible AI',
      'Sociotechnical approach recognising AI risks extend beyond technical factors',
      'Iterative risk management across the full AI lifecycle',
      'Accountability and transparency at organisational and system levels',
      'Fairness, bias mitigation, and equitable outcomes',
      'Privacy protection and security by design',
    ],
    keyRequirements: [
      'GOVERN: Establish AI risk governance policies, roles, and culture',
      'MAP: Identify and categorise AI risks in context',
      'MEASURE: Analyse and assess AI risks using appropriate metrics',
      'MANAGE: Prioritise and treat AI risks with documented controls',
      'Maintain an AI risk register with regular reviews',
      'Engage diverse stakeholders including affected communities',
    ],
    color: '#22c55e',
    icon: '🇺🇸',
  },
  {
    id: 'oecd-ai',
    name: 'OECD Principles on Artificial Intelligence',
    shortName: 'OECD AI Principles',
    issuingBody: 'Organisation for Economic Co-operation and Development (OECD)',
    year: '2019 (updated 2024)',
    summary:
      'The OECD AI Principles are the first intergovernmental standard on AI, adopted by OECD member countries and endorsed by the G20. They establish five value-based principles for responsible AI and five recommendations for governments on AI policy. The principles focus on ensuring AI is beneficial, robust, safe, fair, and accountable throughout its lifecycle.',
    scope: 'Non-binding international principles endorsed by 46+ countries, intended to guide both government AI policy and private sector AI development and deployment.',
    keyPrinciples: [
      'Inclusive growth, sustainable development, and well-being',
      'Human-centred values and fairness',
      'Transparency and explainability',
      'Robustness, security, and safety',
      'Accountability of AI actors',
    ],
    keyRequirements: [
      'Implement meaningful transparency about AI systems and their limitations',
      'Ensure AI systems are robust, secure, and safe throughout their lifecycle',
      'Establish accountability mechanisms for AI actors',
      'Respect the rule of law, human rights, and democratic values',
      'Engage in responsible disclosure of AI incidents',
      'Support international co-operation on AI governance',
    ],
    color: '#a855f7',
    icon: '🌍',
  },
  {
    id: 'unesco-ai',
    name: 'UNESCO Recommendation on the Ethics of AI',
    shortName: 'UNESCO AI Ethics',
    issuingBody: 'United Nations Educational, Scientific and Cultural Organization (UNESCO)',
    year: '2021',
    summary:
      'The UNESCO Recommendation on the Ethics of AI is the first global normative instrument on AI ethics, adopted by all 193 UNESCO member states. It provides a comprehensive ethical framework covering the full AI lifecycle and emphasises human rights, dignity, and the protection of vulnerable groups. It is notable for its explicit attention to environmental sustainability, gender equality, and the rights of indigenous peoples.',
    scope: 'Global normative framework applicable to all UNESCO member states and the organisations operating within them, covering AI research, development, deployment, and governance.',
    keyPrinciples: [
      'Human dignity and human rights as non-negotiable foundations',
      'Fairness and non-discrimination',
      'Transparency and explainability',
      'Safety and security',
      'Responsibility and accountability',
      'Environmental and ecosystem flourishing',
      'Privacy and data protection',
      'Multi-stakeholder and adaptive governance',
    ],
    keyRequirements: [
      'Conduct ethical impact assessments before deploying AI systems',
      'Protect privacy and ensure data governance throughout the AI lifecycle',
      'Ensure AI systems do not perpetuate or amplify discrimination',
      'Provide meaningful human oversight and the right to human review',
      'Assess and mitigate environmental impacts of AI systems',
      'Establish national AI ethics oversight bodies',
    ],
    color: '#f97316',
    icon: '🌐',
  },
  {
    id: 'gdpr',
    name: 'General Data Protection Regulation',
    shortName: 'GDPR',
    issuingBody: 'European Union',
    year: '2018',
    summary:
      'The GDPR is the EU\'s comprehensive data protection law, establishing rights for individuals over their personal data and obligations for organisations that process it. For AI systems, GDPR is particularly relevant where automated decision-making, profiling, or processing of special category data (including biometric, health, and genetic data) is involved. Article 22 grants individuals the right not to be subject to solely automated decisions with significant effects.',
    scope: 'Legally binding regulation applying to any organisation processing personal data of EU/EEA residents, regardless of where the organisation is based.',
    keyPrinciples: [
      'Lawfulness, fairness, and transparency of processing',
      'Purpose limitation — data collected for specified, explicit purposes',
      'Data minimisation — only collect what is necessary',
      'Accuracy and data quality',
      'Storage limitation',
      'Integrity, confidentiality, and security',
      'Accountability of the data controller',
    ],
    keyRequirements: [
      'Establish a lawful basis for all personal data processing',
      'Obtain explicit consent for special category data (biometric, health, genetic)',
      'Conduct Data Protection Impact Assessments (DPIAs) for high-risk processing',
      'Implement the right to explanation for automated decisions (Article 22)',
      'Appoint a Data Protection Officer where required',
      'Maintain records of processing activities',
      'Report data breaches within 72 hours',
    ],
    color: '#eab308',
    icon: '🔒',
  },
  {
    id: 'uk-ai',
    name: 'UK AI Principles',
    shortName: 'UK AI Principles',
    issuingBody: 'UK Government / AI Safety Institute',
    year: '2023',
    summary:
      'The UK AI Principles establish a pro-innovation, context-specific approach to AI governance, assigning responsibility to existing sector regulators rather than creating a single AI regulator. Five cross-sector principles guide regulators and organisations: safety, security and robustness; appropriate transparency and explainability; fairness; accountability and governance; and contestability and redress. The UK AI Safety Institute additionally focuses on frontier AI safety evaluation.',
    scope: 'Applicable to AI systems deployed in the UK, with sector-specific implementation by regulators including the FCA, ICO, CQC, and Ofcom.',
    keyPrinciples: [
      'Safety, security, and robustness',
      'Appropriate transparency and explainability',
      'Fairness',
      'Accountability and governance',
      'Contestability and redress',
    ],
    keyRequirements: [
      'Implement safety testing and monitoring proportionate to risk',
      'Provide meaningful explanations of AI decisions to affected individuals',
      'Assess and mitigate bias and discriminatory outcomes',
      'Establish clear accountability chains for AI systems',
      'Provide accessible mechanisms for individuals to contest AI decisions',
      'Comply with sector-specific regulatory guidance from relevant UK regulators',
    ],
    color: '#ef4444',
    icon: '🇬🇧',
  },
];

async function generateFrameworkPDF(framework: Framework) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;

  const checkY = (needed: number) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const wrapText = (text: string, x: number, maxW: number, lineH: number, color?: [number, number, number]) => {
    if (color) doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    lines.forEach((line: string) => {
      checkY(lineH);
      doc.text(line, x, y);
      y += lineH;
    });
  };

  // Parse color hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };
  const accentRgb = hexToRgb(framework.color);

  // Header banner
  doc.setFillColor(10, 25, 47);
  doc.rect(0, 0, pageW, 50, 'F');

  // Accent stripe
  doc.setFillColor(...accentRgb);
  doc.rect(0, 0, 6, 50, 'F');

  doc.setFontSize(8);
  doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.setFont('helvetica', 'bold');
  doc.text('AI GOVERNANCE FRAMEWORKS LIBRARY', margin, 14);

  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text(framework.name, margin, 26);

  doc.setFontSize(10);
  doc.setTextColor(180, 200, 220);
  doc.setFont('helvetica', 'normal');
  doc.text(`Issuing Body: ${framework.issuingBody}`, margin, 35);
  doc.text(`Year: ${framework.year}  ·  Generated by Sentinel AI Governance Platform`, margin, 42);

  y = 60;

  // Summary section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentRgb);
  doc.text('Overview', margin, y);
  y += 4;
  doc.setDrawColor(...accentRgb);
  doc.setLineWidth(0.4);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  wrapText(framework.summary, margin, contentW, 5.5, [200, 215, 230]);
  y += 4;

  // Scope
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentRgb);
  checkY(16);
  doc.text('Scope & Applicability', margin, y);
  y += 4;
  doc.setDrawColor(...accentRgb);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  wrapText(framework.scope, margin, contentW, 5.5, [200, 215, 230]);
  y += 4;

  // Key Principles
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentRgb);
  checkY(16);
  doc.text('Key Principles', margin, y);
  y += 4;
  doc.setDrawColor(...accentRgb);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  framework.keyPrinciples.forEach(principle => {
    checkY(8);
    doc.setTextColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    doc.text('▸', margin + 2, y);
    doc.setTextColor(200, 215, 230);
    const lines = doc.splitTextToSize(principle, contentW - 12);
    lines.forEach((line: string, li: number) => {
      checkY(6);
      doc.text(line, margin + 10, y);
      if (li < lines.length - 1) y += 5.5;
    });
    y += 6.5;
  });
  y += 2;

  // Key Requirements
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentRgb);
  checkY(16);
  doc.text('Key Requirements & Obligations', margin, y);
  y += 4;
  doc.setDrawColor(...accentRgb);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  framework.keyRequirements.forEach((req, idx) => {
    checkY(8);
    doc.setFillColor(...accentRgb);
    doc.circle(margin + 4, y - 1.5, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text(String(idx + 1), margin + 3.2, y - 0.5);
    doc.setFontSize(10);
    doc.setTextColor(200, 215, 230);
    const lines = doc.splitTextToSize(req, contentW - 12);
    lines.forEach((line: string, li: number) => {
      checkY(6);
      doc.text(line, margin + 10, y);
      if (li < lines.length - 1) y += 5.5;
    });
    y += 7;
  });

  // Footer
  const totalPages = (doc.internal as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(10, 25, 47);
    doc.rect(0, pageH - 14, pageW, 14, 'F');
    doc.setFontSize(8);
    doc.setTextColor(80, 100, 120);
    doc.setFont('helvetica', 'normal');
    doc.text(`${framework.shortName} — Plain-Language Summary  ·  Sentinel AI Governance Platform`, margin, pageH - 6);
    doc.text(`Page ${p} of ${totalPages}`, pageW - margin - 20, pageH - 6);
  }

  doc.save(`${framework.id}-summary.pdf`);
}

function FrameworkCard({ framework }: { framework: Framework }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await generateFrameworkPDF(framework);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-component-id="src/app/frameworks/page.tsx:405:4" data-component-path="src/app/frameworks/page.tsx" data-component-line="405" data-component-end-line="504" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20p-6%20flex%20flex-col%20gap-4%20transition-all%20duration-200%22%7D"
      className="rounded-2xl p-6 flex flex-col gap-4 transition-all duration-200"
      style={{
        background: 'var(--navy-card)',
        border: `1px solid ${framework.color}22`,
        boxShadow: `0 0 0 0 ${framework.color}00`,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.border = `1px solid ${framework.color}55`;
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 24px ${framework.color}12`;
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.border = `1px solid ${framework.color}22`;
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 0 0 ${framework.color}00`;
      }}
    >
      {/* Header */}
      <div data-component-id="src/app/frameworks/page.tsx:422:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="422" data-component-end-line="442" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
        <div data-component-id="src/app/frameworks/page.tsx:423:8" data-component-path="src/app/frameworks/page.tsx" data-component-line="423" data-component-end-line="428" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20rounded-xl%20flex%20items-center%20justify-center%20text-xl%20flex-shrink-0%22%7D"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: `${framework.color}15`, border: `1px solid ${framework.color}30` }}
        >
          {framework.icon}
        </div>
        <div data-component-id="src/app/frameworks/page.tsx:429:8" data-component-path="src/app/frameworks/page.tsx" data-component-line="429" data-component-end-line="441" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
          <div data-component-id="src/app/frameworks/page.tsx:430:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="430" data-component-end-line="438" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20flex-wrap%20mb-0.5%22%7D" className="flex items-center gap-2 flex-wrap mb-0.5">
            <span data-component-id="src/app/frameworks/page.tsx:431:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="431" data-component-end-line="436" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-bold%20px-2%20py-0.5%20rounded-full%22%7D"
              className="text-xs font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${framework.color}15`, color: framework.color, border: `1px solid ${framework.color}30` }}
            >
              {framework.shortName}
            </span>
            <span data-component-id="src/app/frameworks/page.tsx:437:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="437" data-component-end-line="437" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%22%7D" className="text-xs" style={{ color: 'var(--text-muted)' }}>{framework.year}</span>
          </div>
          <h3 data-component-id="src/app/frameworks/page.tsx:439:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="439" data-component-end-line="439" data-component-file="page.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-bold%20leading-snug%22%7D" className="text-sm font-bold leading-snug" style={{ color: 'var(--white-card)' }}>{framework.name}</h3>
          <p data-component-id="src/app/frameworks/page.tsx:440:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="440" data-component-end-line="440" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20mt-0.5%22%7D" className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{framework.issuingBody}</p>
        </div>
      </div>

      {/* Summary */}
      <p data-component-id="src/app/frameworks/page.tsx:445:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="445" data-component-end-line="447" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-relaxed%22%7D" className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {framework.summary}
      </p>

      {/* Key Principles preview */}
      <div data-component-id="src/app/frameworks/page.tsx:450:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="450" data-component-end-line="470" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <div data-component-id="src/app/frameworks/page.tsx:451:8" data-component-path="src/app/frameworks/page.tsx" data-component-line="451" data-component-end-line="453" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20font-semibold%20uppercase%20tracking-wider%20mb-2%22%2C%22textContent%22%3A%22Key%20Principles%22%7D" className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: framework.color }}>
          Key Principles
        </div>
        <div data-component-id="src/app/frameworks/page.tsx:454:8" data-component-path="src/app/frameworks/page.tsx" data-component-line="454" data-component-end-line="469" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-1.5%22%7D" className="flex flex-wrap gap-1.5">
          {framework.keyPrinciples.slice(0, 4).map((p, i) => (
            <span data-component-id="src/app/frameworks/page.tsx:456:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="456" data-component-end-line="462" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20px-2%20py-0.5%20rounded%22%7D"
              key={i}
              className="text-xs px-2 py-0.5 rounded"
              style={{ background: `${framework.color}0d`, color: 'var(--text-secondary)', border: `1px solid ${framework.color}20` }}
            >
              {p}
            </span>
          ))}
          {framework.keyPrinciples.length > 4 && (
            <span data-component-id="src/app/frameworks/page.tsx:465:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="465" data-component-end-line="467" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20px-2%20py-0.5%20rounded%22%2C%22textContent%22%3A%22%2B%20more%22%7D" className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)', border: '1px solid rgba(255,255,255,0.08)' }}>
              +{framework.keyPrinciples.length - 4} more
            </span>
          )}
        </div>
      </div>

      {/* Download button */}
      <button data-component-id="src/app/frameworks/page.tsx:473:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="473" data-component-end-line="503" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22mt-auto%20w-full%20py-2.5%20rounded-lg%20text-sm%20font-semibold%20flex%20items-center%20justify-center%20gap-2%20transition-all%20duration-200%22%7D"
        onClick={handleDownload}
        disabled={downloading}
        className="mt-auto w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
        style={{
          background: downloading ? `${framework.color}18` : `${framework.color}15`,
          color: framework.color,
          border: `1px solid ${framework.color}35`,
          cursor: downloading ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={e => {
          if (!downloading) (e.currentTarget as HTMLButtonElement).style.background = `${framework.color}28`;
        }}
        onMouseLeave={e => {
          if (!downloading) (e.currentTarget as HTMLButtonElement).style.background = `${framework.color}15`;
        }}
      >
        {downloading ? (
          <>
            <span data-component-id="src/app/frameworks/page.tsx:492:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="492" data-component-end-line="492" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-block%20w-4%20h-4%20border-2%20border-current%20border-t-transparent%20rounded-full%20animate-spin%22%7D" className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Generating PDF…
          </>
        ) : (
          <>
            <svg data-component-id="src/app/frameworks/page.tsx:497:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="497" data-component-end-line="499" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path data-component-id="src/app/frameworks/page.tsx:498:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="498" data-component-end-line="498" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download Summary PDF
          </>
        )}
      </button>
    </div>
  );
}

export default function FrameworksPage() {
  return (
    <div data-component-id="src/app/frameworks/page.tsx:510:4" data-component-path="src/app/frameworks/page.tsx" data-component-line="510" data-component-end-line="578" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%22%7D" className="min-h-screen" style={{ background: 'var(--navy)' }}>
      <Header data-component-id="src/app/frameworks/page.tsx:511:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="511" data-component-end-line="511" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />

      <main data-component-id="src/app/frameworks/page.tsx:513:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="513" data-component-end-line="575" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22pt-24%20pb-20%20px-4%20sm%3Apx-6%22%7D" className="pt-24 pb-20 px-4 sm:px-6">
        <div data-component-id="src/app/frameworks/page.tsx:514:8" data-component-path="src/app/frameworks/page.tsx" data-component-line="514" data-component-end-line="574" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">

          {/* Page Header */}
          <div data-component-id="src/app/frameworks/page.tsx:517:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="517" data-component-end-line="532" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-10%22%7D" className="mb-10">
            <div data-component-id="src/app/frameworks/page.tsx:518:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="518" data-component-end-line="525" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-3%22%7D" className="flex items-center gap-2 mb-3">
              <span data-component-id="src/app/frameworks/page.tsx:519:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="519" data-component-end-line="524" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-mono%20px-2%20py-1%20rounded%22%2C%22textContent%22%3A%22FRAMEWORKS%20LIBRARY%22%7D"
                className="text-xs font-mono px-2 py-1 rounded"
                style={{ background: 'rgba(0,212,255,0.1)', color: 'var(--cyan)', border: '1px solid rgba(0,212,255,0.2)' }}
              >
                FRAMEWORKS LIBRARY
              </span>
            </div>
            <h1 data-component-id="src/app/frameworks/page.tsx:526:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="526" data-component-end-line="528" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20mb-3%22%2C%22textContent%22%3A%22AI%20Governance%20Frameworks%22%7D" className="text-3xl md:text-4xl font-bold mb-3" style={{ color: 'var(--white-card)' }}>
              AI Governance Frameworks
            </h1>
            <p data-component-id="src/app/frameworks/page.tsx:529:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="529" data-component-end-line="531" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20max-w-2xl%22%2C%22textContent%22%3A%22Plain-language%20summaries%20of%20the%20major%20international%20AI%20governance%20frameworks.%20Download%20one-page%20PDF%20guides%20for%20each%20framework%20to%20support%20your%20compliance%20and%20governance%20planning.%22%7D" className="text-base max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              Plain-language summaries of the major international AI governance frameworks. Download one-page PDF guides for each framework to support your compliance and governance planning.
            </p>
          </div>

          {/* Stats bar */}
          <div data-component-id="src/app/frameworks/page.tsx:535:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="535" data-component-end-line="550" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20p-4%20mb-8%20flex%20flex-wrap%20gap-6%22%7D"
            className="rounded-xl p-4 mb-8 flex flex-wrap gap-6"
            style={{ background: 'var(--slate)', border: '1px solid rgba(0,212,255,0.1)' }}
          >
            {[
              { label: 'Frameworks Covered', value: FRAMEWORKS.length, color: 'var(--cyan)' },
              { label: 'Legally Binding', value: 2, color: '#ef4444' },
              { label: 'International Standards', value: 3, color: '#22c55e' },
              { label: 'Voluntary Principles', value: 2, color: '#a855f7' },
            ].map(s => (
              <div data-component-id="src/app/frameworks/page.tsx:545:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="545" data-component-end-line="548" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" key={s.label} className="flex items-center gap-3">
                <span data-component-id="src/app/frameworks/page.tsx:546:16" data-component-path="src/app/frameworks/page.tsx" data-component-line="546" data-component-end-line="546" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xl%20font-bold%20font-mono%22%7D" className="text-2xl font-bold font-mono" style={{ color: s.color }}>{s.value}</span>
                <span data-component-id="src/app/frameworks/page.tsx:547:16" data-component-path="src/app/frameworks/page.tsx" data-component-line="547" data-component-end-line="547" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%22%7D" className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Frameworks Grid */}
          <div data-component-id="src/app/frameworks/page.tsx:553:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="553" data-component-end-line="557" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {FRAMEWORKS.map(framework => (
              <FrameworkCard data-component-id="src/app/frameworks/page.tsx:555:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="555" data-component-end-line="555" data-component-file="page.tsx" data-component-name="FrameworkCard" data-component-content="%7B%22elementName%22%3A%22FrameworkCard%22%7D" key={framework.id} framework={framework} />
            ))}
          </div>

          {/* Footer note */}
          <div data-component-id="src/app/frameworks/page.tsx:560:10" data-component-path="src/app/frameworks/page.tsx" data-component-line="560" data-component-end-line="573" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-10%20rounded-xl%20p-5%20flex%20items-start%20gap-3%22%7D"
            className="mt-10 rounded-xl p-5 flex items-start gap-3"
            style={{ background: 'rgba(234,179,8,0.04)', border: '1px solid rgba(234,179,8,0.15)' }}
          >
            <span data-component-id="src/app/frameworks/page.tsx:564:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="564" data-component-end-line="564" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-lg%20flex-shrink-0%22%2C%22textContent%22%3A%22%E2%84%B9%EF%B8%8F%22%7D" className="text-lg flex-shrink-0">ℹ️</span>
            <div data-component-id="src/app/frameworks/page.tsx:565:12" data-component-path="src/app/frameworks/page.tsx" data-component-line="565" data-component-end-line="572" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-2%22%7D" className="flex flex-col gap-2">
              <p data-component-id="src/app/frameworks/page.tsx:566:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="566" data-component-end-line="568" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20leading-relaxed%22%2C%22textContent%22%3A%22These%20summaries%20are%20plain-language%20guides%20intended%20to%20support%20AI%20governance%20planning%20and%20do%20not%20constitute%20legal%20advice.%20They%20are%20not%20the%20official%20legal%20text%20of%20each%20framework.%20Before%20making%20compliance%20decisions%2C%20consult%20the%20original%20framework%20documents%20and%20qualified%20legal%20counsel.%22%7D" className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                These summaries are plain-language guides intended to support AI governance planning and do not constitute legal advice. They are not the official legal text of each framework. Before making compliance decisions, consult the original framework documents and qualified legal counsel.
              </p>
              <p data-component-id="src/app/frameworks/page.tsx:569:14" data-component-path="src/app/frameworks/page.tsx" data-component-line="569" data-component-end-line="571" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20leading-relaxed%22%2C%22textContent%22%3A%22Framework%20mapping%20is%20provided%20for%20governance%20decision%20support%20and%20does%20not%20constitute%20legal%20advice%2C%20regulatory%20certification%20or%20conformity%20assessment.%20Sentinel%20uses%20the%20following%20language%3A%20%5C%22aligned%20with%5C%22%2C%20%5C%22informed%20by%5C%22%2C%20%5C%22mapped%20against%5C%22%2C%20%5C%22supports%20readiness%5C%22%2C%20%5C%22supports%20internal%20assessment%5C%22.%20Sentinel%20does%20not%20claim%20to%20be%20%5C%22certified%5C%22%2C%20%5C%22regulator-approved%5C%22%2C%20%5C%22formally%20compliant%5C%22%20or%20%5C%22conformity%20assessed%5C%22%20against%20any%20framework.%22%7D" className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                <strong data-component-id="src/app/frameworks/page.tsx:570:16" data-component-path="src/app/frameworks/page.tsx" data-component-line="570" data-component-end-line="570" data-component-file="page.tsx" data-component-name="strong" data-component-content="%7B%22elementName%22%3A%22strong%22%2C%22textContent%22%3A%22Sentinel%20framework%20mapping%20disclaimer%3A%22%7D" style={{ color: 'var(--text-secondary)' }}>Sentinel framework mapping disclaimer:</strong> Framework mapping is provided for governance decision support and does not constitute legal advice, regulatory certification or conformity assessment. Sentinel uses the following language: "aligned with", "informed by", "mapped against", "supports readiness", "supports internal assessment". Sentinel does not claim to be "certified", "regulator-approved", "formally compliant" or "conformity assessed" against any framework.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-component-id="src/app/frameworks/page.tsx:577:6" data-component-path="src/app/frameworks/page.tsx" data-component-line="577" data-component-end-line="577" data-component-file="page.tsx" data-component-name="Footer" data-component-content="%7B%22elementName%22%3A%22Footer%22%7D" />
    </div>
  );
}
